import { CustomPipelineOptions, GetListResult } from '@/providers/dataProvider';
import { adminRpcClient } from '@/providers/backendClient';

interface ReportItem {
  name: string;
  displayName?: string;
  path: string;
  type: 'file' | 'folder';
  lastModified?: string;
  size?: number;
  children?: ReportItem[];
}

interface ReportRecord {
  id: string;
  title: string; // Format: "YYYY-MM-DD - {number} - {PascalCaseName}"
  path: string;
  lastModified?: string;
}

/**
 * Parses a report folder name to create a formatted title
 * Format: YYYY-MM-DD-{number}-{PascalCaseName}
 * Returns: "YYYY-MM-DD - {number} - {PascalCaseName}"
 */
const parseReportFolder = (item: ReportItem): ReportRecord | null => {
  // Only process folders
  if (item.type !== 'folder') {
    return null;
  }

  // Pattern: YYYY-MM-DD-{number}-{PascalCaseName}
  const match = item.name.match(/^(\d{4}-\d{2}-\d{2})-(\d+)-(.+)$/);
  if (!match) {
    return null; // Not a report folder
  }

  const [, date, numberStr, pascalCaseName] = match;

  // Convert PascalCase to human-readable: "ExpiredAuth0Tokens" -> "Expired Auth0 Tokens"
  const humanReadable = pascalCaseName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^ /, '') // Remove leading space
    .trim();

  // Format: "YYYY-MM-DD - {number} - {PascalCaseName}"
  const title = `${date} - ${numberStr} - ${humanReadable}`;

  return {
    id: item.path,
    title,
    path: item.path,
    lastModified: item.lastModified,
  };
};

export const reportsCustomFetcher = async (
  options: CustomPipelineOptions,
): Promise<GetListResult> => {
  // Fetch root-level reports using RPC
  const result = (await adminRpcClient(
    'PUBLIC_ADMIN_reports_list',
    [''],
    {
      ...(options.signal ? { signal: options.signal } : {}),
    },
  )) as { success: boolean; items?: ReportItem[] };

  if (!result.success || !result.items) {
    return { results: [], total: 0 };
  }

  // Filter to only folders and parse them
  const records = result.items
    .map(parseReportFolder)
    .filter((r): r is ReportRecord => r !== null);

  // Apply sorting - sort by title (which contains date and number, so natural sort works)
  let sortedRecords = [...records];
  if (options.sort) {
    const { field, order } = options.sort;
    sortedRecords.sort((a, b) => {
      let aVal: any = a[field as keyof ReportRecord];
      let bVal: any = b[field as keyof ReportRecord];

      // Default string comparison
      aVal = aVal || '';
      bVal = bVal || '';
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return order === 'ASC' ? comparison : -comparison;
    });
  } else {
    // Default sort: title descending (which naturally sorts by date DESC, then number DESC)
    sortedRecords.sort((a, b) => {
      return b.title.localeCompare(a.title); // Descending
    });
  }

  // Apply pagination
  const startIndex = (options.page - 1) * options.perPage;
  const endIndex = startIndex + options.perPage;
  const paginatedRecords = sortedRecords.slice(startIndex, endIndex);

  // Ensure all records have an 'id' field (required by react-admin)
  const recordsWithId = paginatedRecords.map(record => {
    if (!record.id) {
      // Fallback: use path as id if id is missing
      return { ...record, id: record.path || `report-${record.title}` };
    }
    return record;
  });

  return {
    results: recordsWithId,
    total: sortedRecords.length,
  };
};

